import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { clampNumber, convertPxToRem } from "../../../../../utils";
import theme from "../../../../../theme";
import IconLoader from "../../../../Common/components/IconLoader";
import { useHealthScoreRatingColour } from "../hooks/useHealthScoreRatingColour";

interface HealthScoreIndicatorProps {
  healthScore: number;
}

interface HealthScoreContainerSpacingProps {
  addContainerSpacing?: boolean;
  subtitleRightSpacing?: number;
}

type HealthScoreContainerProps = HealthScoreIndicatorProps &
  HealthScoreContainerSpacingProps;

enum HealthScoreRating {
  NONE = 0,
  LOWER_AVERAGE_SCORE = 20,
  UPPER_AVERAGE_SCORE = 60,
  GOOD = 100,
}

function HealthScoreContainerSpacing() {
  return (
    <Box
      sx={{
        width: ".5px",
        borderRadius: 2,
        background: (theme) => theme.palette.primary[300],
        height: "5px",
      }}
    />
  );
}

function HealthScoreContainer({
  healthScore,
  subtitleRightSpacing,
  addContainerSpacing = true,
}: HealthScoreContainerProps) {
  return (
    <>
      {addContainerSpacing && <HealthScoreContainerSpacing />}
      <Box
        sx={{
          width: "1.5px",
          borderRadius: 2,
          background: (theme) => theme.palette.primary[500],
          height: "6px",
          position: "relative",
        }}
      >
        <Typography
          component="p"
          variant="subtitle2"
          sx={{
            position: "absolute",
            top: (theme) => theme.spacing(1),
            right: convertPxToRem(subtitleRightSpacing || 0),
            color: (theme) => theme.palette.primary[500],
          }}
        >
          {healthScore}
        </Typography>
      </Box>
    </>
  );
}

function HealthScoreUnitScale() {
  const markers = [
    {
      value: HealthScoreRating.NONE,
      subtitleRightSpacing: -5,
      addContainerSpacing: true,
      transform: "translateX(0)",
    },
    {
      value: HealthScoreRating.LOWER_AVERAGE_SCORE,
      subtitleRightSpacing: -5,
      addContainerSpacing: true,
      transform: "translateX(-50%)",
    },
    {
      value: HealthScoreRating.UPPER_AVERAGE_SCORE,
      subtitleRightSpacing: -5,
      addContainerSpacing: true,
      transform: "translateX(-50%)",
    },
    {
      value: HealthScoreRating.GOOD,
      subtitleRightSpacing: 0,
      addContainerSpacing: true,
      transform: "translateX(-100%)",
    },
  ];

  return (
    <Box position="relative" width="100%" height={convertPxToRem(18)}>
      {markers.map((marker) => (
        <Box
          key={marker.value}
          sx={{
            position: "absolute",
            left: `${marker.value}%`,
            transform: marker.transform,
          }}
        >
          <HealthScoreContainer
            healthScore={marker.value}
            addContainerSpacing={marker.addContainerSpacing}
            subtitleRightSpacing={marker.subtitleRightSpacing}
          />
        </Box>
      ))}
    </Box>
  );
}

export default function HealthScoreIndicator({
  healthScore,
}: HealthScoreIndicatorProps) {
  const { t } = useTranslation();
  const computedHealthScore = clampNumber(healthScore, 0, 100);
  const { healthScoreRatingColour } =
    useHealthScoreRatingColour(computedHealthScore);

  return (
    <Stack
      component="article"
      spacing={4}
      aria-label={t("HealthScoreIndicator.panel.label")}
    >
      <Stack spacing={6}>
        <Box>
          <Stack direction="row" position="relative" width="100%">
            <Box
              height={10}
              width="20%"
              borderRadius={"5px 0 0 5px"}
              bgcolor="error.main"
            />
            <Box height={10} width="40%" bgcolor="secondary.main" />
            <Box
              height={10}
              width="40%"
              borderRadius="0px 5px 5px 0px"
              bgcolor="accent1.main"
            />
            <Box
              position="absolute"
              sx={{
                transform: `translateX(${convertPxToRem(-18)})`,
                bottom: (theme) => theme.spacing(1.5),
                height: convertPxToRem(29),
                left: `${computedHealthScore}%`,
              }}
              data-testid="health-score-pin-container"
            >
              <IconLoader
                icon="HealthPin"
                sx={{
                  fontSize: convertPxToRem(38),
                  color: healthScoreRatingColour,
                }}
              />
            </Box>
          </Stack>
          <HealthScoreUnitScale />
        </Box>
      </Stack>
    </Stack>
  );
}
